import { isTestLevelId } from '../data/ids';
import type { ResolvedLevel } from '../data/load-level';
import { ACTIVATION_MARGIN, CHECKPOINT_POINTS, CLEAN_RUN_POINTS, COLUMN_POINTS, RESPAWN_FALLBACK_Y, PLAYER_SCREEN_X, SIM_HZ, VIEW_WIDTH, WORLD_TUNING, GROUND_Y, PLAYER_HITBOX, JUMP_ZONE, LEVEL_COMPOSITION } from '../data/tuning';
import { CHECKPOINT_RADIO_LINE, createCheckpointTrack, crossCheckpoints, type CheckpointTrack } from './checkpoints';
import { collidesWithColumn, columnPieces, hasPassed } from './columns';
import { updateIntercepts, type InterceptWarning } from './intercept';
import { collectArmor, collectBribe, collectDollar, collectScapular, isCollectable } from './pickups';
import { ENEMIES, isEnemyType } from '../data/enemies';
import { EN03, lockHeight, stepHitmanPlane } from './enemies/hitman-plane';
import { EN22, LAUNCH_TICKS, createAntenna, touchesZone, type AntennaState } from './enemies/antenna';
import './boss/register-patterns';
import { attachRhythm, type RhythmHandle } from './rhythm/rhythm-system';
import type { RhythmConfig } from './rhythm/rhythm-clock';
import { collectAttackPickups, attackCollisions, createAttackField, resetAttacks, stepAttacks, type AttackField } from './boss/attacks';
import { isVulnerable } from './boss/boss-run';
import { createDecoyField, stepDecoys, type DecoyField } from './boss/decoys';
import { BOSSES, type BossDef } from '../data/boss';
import { bossX, createBossFight, enterArena, isDamaging, stepBossFight, bossBody, type BossFight } from './boss/boss-fight';
import { createCableCar, stepCableCar, type CableCarState } from './enemies/cable-car';
import { createHippo, stepHippo, type HippoState } from './enemies/hippo';
import { flockCircles } from './enemies/seagull-flock';
import { createCropDuster, dusterX, puffCircle, stepCropDuster, type CropDusterState } from './enemies/crop-duster';
import { armCircles, craneStructure, craneView, createCrane, stepCrane, type CraneState } from './enemies/port-crane';
import { createParaglider, stepParaglider, type ParagliderState } from './enemies/paraglider';
import { createSombreroThrower, sombreroCircle, sombreroPosition, stepSombreroThrower, type SombreroThrowerState } from './enemies/sombrero';
import { chimneyX, createTrain, stepTrain, trainBody, trainX, type TrainState } from './hazards/en33-train';
import { createGoldenEagle, eagleCircle, eaglePosition, startWarning, stepEagle, type GoldenEagleState } from './enemies/golden-eagle';
import { createBorderDrone, droneBody, droneY, type BorderDroneState } from './enemies/border-drone';
import { barrelCircle, barrelY, createTunnel, DUST_TICKS, fire, stepTunnel, trigger, type TunnelState } from './enemies/tunnel';
import { createHelicopter, stepHelicopter, stepProjectiles, EN02, type HelicopterState } from './enemies/law-helicopter';
import { createWallSearchlight, stepWallProjectiles, stepWallSearchlight, type WallSearchlightState } from './enemies/wall-searchlight';
import { aabbOverlap, circleAabbOverlap, createAabb, createCircle } from './collision';
import { stepGallinazo } from './enemies/gallinazo';
import { groundContact, playerAabb } from './player';
import { gradeJump, startJump, stepPilot, type JumpState } from './jump/jump';
import type { JumpGrade } from './run';
import { createRunState, type RunState } from './run';
import { completeLevel } from './progression';
import { DERRIBO_TICKS, acceptsFlap, isHolding, scrolls, transition, type LevelState } from './level-state';
import { TickPhase, createCoreWorld, type CoreWorld, type CoreWorldOptions, type TickContext } from './tick';

/** Something placed in the level, in absolute x. Only active entities are updated and checked. */
export interface RunEntity {
  readonly kind: 'column' | 'pickup' | 'enemy';
  /** Index in the matching list of the resolved level (columns, pickups or enemies). */
  readonly index: number;
  readonly x: number;
  active: boolean;
  /** Phase 3 updates received while active. */
  updates: number;
}

export interface LevelRun {
  readonly level: ResolvedLevel;
  readonly core: CoreWorld;
  /** Scroll speed of the level (u/s); tests may change it. */
  scrollSpeed: number;
  readonly entities: RunEntity[];
  /** x of the last placement and scrolling ticks since: x = originX + scrollTicks × speed / 60. */
  originX: number;
  scrollTicks: number;
  /** Level state already announced with LEVEL_STATE_CHANGED (a crash is detected against it). */
  observedState: LevelState;
  /** Ticks of DERRIBO left before RESPAWN or FAIL. */
  derriboRemaining: number;
  /** Indexes of the columns already passed: each one scores once (re-armed on respawn by #39). */
  readonly passedColumns: Set<number>;
  /** Indexes of the pickups already collected: never twice (regenerated on respawn by #39). */
  readonly collectedPickups: Set<number>;
  /** Checkpoints of the level in x order, with the active one (GDD §6.2). */
  readonly checkpoints: CheckpointTrack;
  /** Runtime position of each enemy of the level, regenerated on respawn. Behaviours arrive with #71+. */
  readonly enemies: EnemyState[];
  /** The INTERCEPT warnings showing now (GDD §8.1, issue #83). */
  intercepts: InterceptWarning[];
  /** The boss fight of the level, if it has one (GDD §9.1, issue #89). */
  boss: BossFight | null;
  /** The decoys of the boss fight (GDD §9.1, issue #90); null in a level without a boss. */
  decoys: DecoyField | null;
  /** What the patterns of the boss throw: coins, leads, plush hippos and the phase-3 helicopter (issue #95). */
  attacks: AttackField | null;
  /** The rhythm clock of the level's music (JEFE-2 and its salsa, issue #109); null when the level has no rhythm. */
  rhythm: RhythmHandle | null;
  /** x where the boss arena starts: the boss enters when the plane reaches it. */
  arenaStart: number;
  /** The US sky after the boss (GDD §11.1, issue #92): where it starts and where the jump zone begins. */
  usSky: { startX: number; jumpX: number } | null;
  /**
   * The jump zone: the height the autopilot leaves from and its ticks since it engaged, the view it froze
   * (the plane then crosses the screen, #93) and the centre of the X on the ground.
   */
  jumpZone: { fromY: number; ticks: number; viewLeft: number; targetX: number } | null;
  /** The jump of the pilot (issue #93); after it, `core.player` is the pilot and `planeX` the empty plane. */
  jump: JumpState | null;
  planeX: number | null;
  /** The persistent part of the game: level, checkpoint, unlocks and records (GDD §6.4). */
  readonly state: RunState;
  /** Deaths already reflected in state.cleanRun. */
  deathsSeen: number;
}

export interface EnemyState {
  readonly index: number;
  readonly type: string;
  x: number;
  y: number;
  /** Tick on which it first became active; behaviours count their time from it. */
  activatedAt: number | null;
  /** EN-03 (issue #85): the height locked at the INTERCEPT warning, and whether it has taken off. */
  targetY: number | null;
  /** EN-02 (issue #84): the searchlight, its exposure counter and its projectiles. */
  helicopter: HelicopterState | null;
  launched: boolean;
  /** EN-04 (issue #86): the descent and whether it has landed. */
  paraglider: ParagliderState | null;
  /** EN-11 (issue #87): the phase of its jump. */
  hippo: HippoState | null;
  /** EN-12 (issue #88): the ticks of the cabin along its cable. */
  cableCar: CableCarState | null;
  /** EN-21 (issue #104): the ticks of the port crane, from which its arm and beacon are computed. */
  crane: CraneState | null;
  /** EN-31 (issue #116): its flight, its spray timer and the puffs of its toxic cloud. */
  cropDuster: CropDusterState | null;
  /** EN-22 (issue #105): the surveillance antenna and the hitman plane it called. */
  antenna: AntennaState | null;
  /** EN-32 (issue #117): Don Nieto's balcony and the sombrero in flight. */
  sombrero: SombreroThrowerState | null;
  /** EN-33 (issue #118): the tequila train and its smoke columns (render-only). */
  train: TrainState | null;
  /** EN-41 (issue #127): the golden eagle's warning and dive. */
  eagle: GoldenEagleState | null;
  /** EN-42 (issue #128): the border drone's vertical patrol. */
  drone: BorderDroneState | null;
  /** EN-43 (issue #129): the tunnel trapdoor and its barrel. */
  tunnel: TunnelState | null;
  /** EN-44 (issue #130): a fixed wall searchlight sweeping in sync with its group. */
  searchlight: WallSearchlightState | null;
}

/**
 * Altitude to start or respawn at x (GDD §6.2, rule 4): the centre of the gap of the first column
 * ahead, or the middle of the playable band when no column lies ahead.
 */
export function entryAltitude(level: ResolvedLevel, x: number): number {
  const next = level.columns.filter((column) => column.x > x).sort((a, b) => a.x - b.x)[0];
  return next ? next.gapY : RESPAWN_FALLBACK_Y;
}

/**
 * The level end (GDD §6.2 rule 7, §12): the clean-run bonus, the checkpoints cleared, the progression applied
 * (with the grade of El Salto in boss levels, #93) and LEVEL_COMPLETED.
 */
function finishLevel(run: LevelRun, context: TickContext, jumpGrade: JumpGrade | null): void {
  const { core: current, level } = run;
  // The clean-run bonus is part of the level score (GDD §12).
  const cleanRun = run.state.cleanRun;
  if (cleanRun) current.pendingScore += CLEAN_RUN_POINTS;
  const totalScore = current.score + current.pendingScore;
  const levelId = level.def.id;
  const levelScore = totalScore - run.state.scoreAtLevelStart;
  current.levelCompleted = true;
  for (const checkpoint of run.checkpoints.checkpoints) checkpoint.active = false;
  run.checkpoints.activeIndex = -1;
  if (!run.state.test) completeLevel(run.state, { levelScore, totalScore, cleanRun, jumpGrade });
  context.emit({ type: 'LEVEL_COMPLETED', levelId, levelScore, cleanRun, ...(jumpGrade && { jumpGrade }) });
  transition(run, 'LEVEL_END', context);
}

/** The pilot jumps with La Bolsa (GDD §11.1 step 3); a forced jump is always FORZOSO (§11.2). */
function jump(run: LevelRun, forced: boolean, context: TickContext): void {
  run.jump = startJump(forced);
  run.planeX = run.core.player.x;
  run.core.player = { ...run.core.player, vy: 0 };
  context.emit({ type: 'jump:jumped', x: run.core.player.x, forced });
}

/** The landing (GDD §11.1 steps 5 and 6): the distance to the X grades the jump, then the level ends. */
function land(run: LevelRun, context: TickContext): void {
  const distance = Math.abs((run.jump!.landingX ?? run.core.player.x) - run.jumpZone!.targetX);
  const { grade, bonus } = gradeJump(distance, run.jump!.forced);
  run.core.pendingScore += bonus;
  context.emit({ type: 'jump:delivered', grade, distance, bonus, forced: run.jump!.forced });
  finishLevel(run, context, grade);
}

/** Centre of the plane resting on the ground line (the US sky, issue #92). */
const GROUND_REST_Y = GROUND_Y + PLAYER_HITBOX.height / 2 + 1e-6;

/**
 * The corridor opened by the retreat of the boss (GDD §11.1, §9.1): the plane goes to the US sky segment of
 * the level (the rest of the arena is skipped) or stays where it is if there is none, and the next 120 u
 * are the US sky. The border sign and the US palette are announced with border:crossed.
 */
function openUsSky(run: LevelRun, context: TickContext): void {
  if (run.usSky || !transition(run, 'CORRIDOR_OPEN', context)) return;
  const segment = run.level.segmentStarts.find((candidate) => candidate.kind === 'us-sky');
  const startX = Math.max(run.core.player.x, segment?.start ?? run.core.player.x);
  if (startX > run.core.player.x) placePlane(run, startX);
  // The decoys still in flight are removed with the boss: nothing follows the plane across the border.
  if (run.decoys) run.decoys.decoys.length = 0;
  if (run.attacks) resetAttacks(run.attacks);
  run.usSky = { startX, jumpX: startX + (segment?.length ?? LEVEL_COMPOSITION.usSkyLength) };
  context.emit({ type: 'border:crossed', x: startX, sign: 'world.sign.usa', skyTheme: 'usa' });
}

export function initialEnemies(level: ResolvedLevel): EnemyState[] {
  return level.enemies.map((enemy, index) => {
    const helicopter = enemy.type === 'EN-02' ? createHelicopter(enemy.y) : null;
    // The helicopter arrives from above: it starts higher than its hover point.
    const y = helicopter ? enemy.y + helicopter.arrivalLeft : enemy.y;
    const paraglider = enemy.type === 'EN-04' ? createParaglider(enemy.x) : null;
    const hippo = enemy.type === 'EN-11' ? createHippo(enemy.params) : null;
    return { index, type: enemy.type, x: enemy.x, y, activatedAt: null, targetY: null, launched: false, helicopter, paraglider, hippo, cableCar: enemy.type === 'EN-12' ? createCableCar() : null, crane: enemy.type === 'EN-21' ? createCrane() : null, cropDuster: enemy.type === 'EN-31' ? createCropDuster() : null, antenna: enemy.type === 'EN-22' ? createAntenna() : null, sombrero: enemy.type === 'EN-32' ? createSombreroThrower() : null, train: enemy.type === 'EN-33' ? createTrain() : null, eagle: enemy.type === 'EN-41' ? createGoldenEagle() : null, drone: enemy.type === 'EN-42' ? createBorderDrone() : null, tunnel: enemy.type === 'EN-43' ? createTunnel() : null, searchlight: enemy.type === 'EN-44' ? createWallSearchlight(enemy.params) : null };
  });
}

/** Distance from the left edge of the view to the plane: 30 % of 21.333 = 6.4 u (GDD §5.2). */
export const PLANE_OFFSET_IN_VIEW = VIEW_WIDTH * PLAYER_SCREEN_X;

export function viewOf(run: LevelRun): { left: number; right: number } {
  // In the jump zone the view stops and the plane crosses the screen towards its right edge (#93).
  const left = run.jumpZone?.viewLeft ?? run.core.player.x - PLANE_OFFSET_IN_VIEW;
  return { left, right: left + VIEW_WIDTH };
}

/** 0 at the start, 1 at the end of the level, for the route telemetry (#59). */
export function progressOf(run: LevelRun): number {
  return Math.min(Math.max(run.core.player.x / run.level.length, 0), 1);
}

function updateActivation(run: LevelRun): void {
  const { left, right } = viewOf(run);
  for (const entity of run.entities) {
    // Enemies move: their window uses where they are now, not where they were placed.
    const enemy = entity.kind === 'enemy' ? run.enemies[entity.index] : undefined;
    const x = enemy ? enemy.x : entity.x;
    entity.active = x <= right + ACTIVATION_MARGIN && x >= left - ACTIVATION_MARGIN;
    if (enemy && entity.active && enemy.activatedAt === null) enemy.activatedAt = run.core.tick;
  }
}

/** Moves the plane to x in level coordinates; the scroll continues from there. */
export function placePlane(run: LevelRun, x: number): void {
  run.originX = x;
  run.scrollTicks = 0;
  run.core.player = { ...run.core.player, x };
  updateActivation(run);
}

/**
 * Starts a level (GDD §10.2, §5.2). The plane stays at 30 % of the view while the world scrolls: in
 * level coordinates, its x grows by the scroll speed in phase 3 of every tick. x is computed from the
 * tick count instead of adding floats, so 600 ticks at 5.0 u/s are exactly 50.0 u.
 */
export function createRun(
  level: ResolvedLevel,
  options: Omit<CoreWorldOptions, 'player'> & { state?: Partial<RunState>; initialState?: LevelState; boss?: BossDef; rhythm?: RhythmConfig },
): LevelRun {
  // The fight of the level: given by the caller (tests) or by the boss of its data (issue #89).
  const bossDef = options.boss ?? (level.def.bossId ? BOSSES[level.def.bossId] : undefined);
  const core = createCoreWorld({ ...options, player: { x: 0, y: entryAltitude(level, 0), vy: 0 } });
  const entities: RunEntity[] = [
    ...level.columns.map((column, index) => ({ kind: 'column' as const, index, x: column.x })),
    ...level.pickups.map((pickup, index) => ({ kind: 'pickup' as const, index, x: pickup.x })),
    ...level.enemies.map((enemy, index) => ({ kind: 'enemy' as const, index, x: enemy.x })),
  ].map((entity) => ({ ...entity, active: false, updates: 0 }));

  const run: LevelRun = {
    level,
    core,
    scrollSpeed: level.def.scrollSpeed,
    entities,
    originX: 0,
    scrollTicks: 0,
    observedState: options.initialState ?? 'INTRO',
    derriboRemaining: 0,
    passedColumns: new Set(),
    collectedPickups: new Set(),
    checkpoints: createCheckpointTrack(level.checkpoints),
    enemies: initialEnemies(level),
    intercepts: [],
    boss: bossDef ? createBossFight(bossDef) : null,
    decoys: bossDef ? createDecoyField((options.seed ?? 0) + 0xdec0) : null,
    attacks: bossDef ? createAttackField((options.seed ?? 0) + 0xa77ac) : null,
    rhythm: options.rhythm ? attachRhythm(core, options.rhythm) : null,
    arenaStart: level.segmentStarts.find((segment) => segment.kind === 'boss-arena')?.start ?? 0,
    usSky: null,
    jumpZone: null,
    jump: null,
    planeX: null,
    // OP-000 is not a campaign id: its run state holds a placeholder level and is marked as a test run.
    state: createRunState(isTestLevelId(level.def.id) ? '1-1' : level.def.id, {
      ...(isTestLevelId(level.def.id) ? { test: true } : {}),
      ...options.state,
    }),
    deathsSeen: core.deaths,
  };
  core.levelState = run.observedState;
  core.score = run.state.scoreAtLevelStart;
  const columnWidth = WORLD_TUNING[level.def.world].columnWidth;
  const pieces = level.columns.map((column) => columnPieces(column, columnWidth));
  const activeColumns = () => run.entities.filter((entity) => entity.kind === 'column' && entity.active);

  // Phase 1, after the input: flaps only count in INTRO, VUELO and REAPARICION; the first flap of
  // INTRO or REAPARICION starts the flight and is applied by the physics of this same tick.
  core.systems.register(TickPhase.Input, (current, context) => {
    if (!acceptsFlap(current.levelState)) context.flap = false;
    else if (context.flap && isHolding(current.levelState)) transition(run, 'FLAP', context);
    // The flap that counts is announced for the sound of the engine (issue #76); the core knows no audio.
    if (context.flap) context.emit({ type: 'FLAP' });
    // In the jump zone the button is the jump (GDD §11.1 step 3); once jumped it does nothing (§11.3).
    if (current.levelState === 'SALTO' && !run.jump && context.actions.includes('FLAP')) jump(run, false, context);
  });

  // Phase 3: the world scrolls in VUELO (and past the finish in COMPLETADO); it stops in the other states.
  core.systems.register(TickPhase.Entities, (current, context) => {
    if (scrolls(current.levelState)) run.scrollTicks += 1;
    const x = run.originX + (run.scrollTicks * run.scrollSpeed) / SIM_HZ;
    // After the jump the plane flies on without the pilot, who moves on its own (#93).
    if (run.jump) run.planeX = x;
    else current.player = { ...current.player, x };
    updateActivation(run);
    // Nobody waits forever: at the right edge of the view the pilot jumps anyway (GDD §11.3).
    if (current.levelState === 'SALTO' && !run.jump && current.player.x >= viewOf(run).right - PLAYER_HITBOX.width / 2) {
      jump(run, true, context);
    }
    // EN-03 flies from its warning on, wherever it is: outside the active window too (issue #85).
    if (current.levelState === 'VUELO') {
      for (const enemy of run.enemies) if (enemy.type === 'EN-03' && enemy.launched) enemy.x = stepHitmanPlane(enemy.x);
    }
    // EN-04 (issue #86): once it has been in the view it keeps descending, wherever it is.
    if (current.levelState === 'VUELO') {
      for (const enemy of run.enemies) {
        if (!enemy.paraglider || enemy.activatedAt === null) continue;
        const spawn = level.enemies[enemy.index]!;
        Object.assign(enemy, stepParaglider(enemy.paraglider, spawn, spawn.params));
      }
    }
    // EN-12 (issue #88): the cabin goes back and forth along its cable from the first time it is in the view.
    if (current.levelState === 'VUELO') {
      for (const enemy of run.enemies) {
        if (!enemy.cableCar || enemy.activatedAt === null) continue;
        const spawn = level.enemies[enemy.index]!;
        Object.assign(enemy, stepCableCar(enemy.cableCar, spawn, spawn.params));
      }
    }
    // EN-22 (issue #105): one second after the trigger the called EN-03 takes off, (scroll + 4) u beyond the right
    // edge so it enters 2 s after the trigger, at the height the pilot has now; it then flies like any EN-03.
    if (current.levelState === 'VUELO') {
      const { left, right } = viewOf(run);
      for (const enemy of run.enemies) {
        const antenna = enemy.antenna;
        if (!antenna || antenna.phase !== 'TRIGGERED') continue;
        if (antenna.hitman) {
          antenna.hitman.x = stepHitmanPlane(antenna.hitman.x);
          if (antenna.hitman.x < left - EN03.hitboxWidth) {
            antenna.hitman = null;
            antenna.phase = 'SPENT';
          }
        } else if (current.tick - antenna.triggeredAt! >= LAUNCH_TICKS) {
          antenna.hitman = { x: right + (run.scrollSpeed + EN03.extraSpeed) * EN22.warningLeadSeconds, y: lockHeight(current.player.y) };
          antenna.spawned += 1;
          context.emit({ type: 'antenna:hitman', enemyIndex: enemy.index, y: antenna.hitman.y });
        }
      }
    }
    // EN-21 (issue #104): the crane's clock runs from the first time it is in the view; the beacon is
    // announced when it lights up, one second before each rotation.
    if (current.levelState === 'VUELO') {
      for (const enemy of run.enemies) {
        if (!enemy.crane || enemy.activatedAt === null) continue;
        const params = level.enemies[enemy.index]!.params;
        const before = craneView(enemy.crane.ticks, params).beacon;
        stepCrane(enemy.crane);
        if (!before && craneView(enemy.crane.ticks, params).beacon) context.emit({ type: 'ENEMY_ROTATION_WARNING', enemyType: 'EN-21', enemyIndex: enemy.index });
      }
    }
    // EN-31 (issue #116): the crop duster flies with the player and sprays its cloud while it is on screen; the puffs
    // age and expire wherever they are, so the cloud outlives the plane.
    if (current.levelState === 'VUELO') {
      for (const enemy of run.enemies) {
        if (!enemy.cropDuster || enemy.activatedAt === null) continue;
        enemy.x = dusterX(level.enemies[enemy.index]!.x, enemy.cropDuster.ticks + 1);
        const onScreen = run.entities.some((entity) => entity.kind === 'enemy' && entity.index === enemy.index && entity.active);
        if (stepCropDuster(enemy.cropDuster, enemy, onScreen)) context.emit({ type: 'en31:spray', enemyIndex: enemy.index });
      }
    }
    // EN-43 (issue #129): the tunnel warns with dust when the plane comes within trigger distance, then launches a
    // barrel straight up to an apex; the barrel falls back and the trapdoor closes. The dust and the trapdoor never
    // harm. One launch per pass; reusable by JEFE-4 (#133).
    if (current.levelState === 'VUELO') {
      for (const enemy of run.enemies) {
        if (!enemy.tunnel) continue;
        if (enemy.tunnel.phase === 'closed') {
          if (trigger(enemy.tunnel, enemy.x - current.player.x)) {
            context.emit({ type: 'enemy-warning', enemyType: enemy.type, enemyIndex: enemy.index, x: enemy.x });
            enemy.tunnel.flight = 0;
          }
        } else if (enemy.tunnel.phase === 'dust') {
          enemy.tunnel.flight += 1;
          if (enemy.tunnel.flight >= DUST_TICKS && fire(enemy.tunnel)) {
            context.emit({ type: 'hazard-launched', enemyType: enemy.type, enemyIndex: enemy.index });
          }
        } else if (stepTunnel(enemy.tunnel)) {
          context.emit({ type: 'en43:closed', enemyIndex: enemy.index });
        }
      }
    }
    // EN-42 (issue #128): the border drone patrols vertically between two heights with an exact rebound; it does
    // not move in x with respect to the world (it drifts with the scroll, like a stationary EN-02).
    if (current.levelState === 'VUELO') {
      for (const enemy of run.enemies) {
        if (!enemy.drone || enemy.activatedAt === null) continue;
        const params = level.enemies[enemy.index]!.params ?? {};
        const startY = level.enemies[enemy.index]!.y;
        enemy.drone.ticks += 1;
        enemy.y = droneY(startY, enemy.drone.ticks, { yMin: params.yMin ?? startY, yMax: params.yMax ?? startY, dir: params.dir ?? 1 });
      }
    }
    // EN-41 (issue #127): the golden eagle warns (screech and shadow) when the plane crosses its trigger line,
    // locks the pilot's position and dives 1.0 s later. It enters from above, so no INTERCEPT. It never hits the
    // ground: it levels off and leaves the screen.
    if (current.levelState === 'VUELO') {
      for (const enemy of run.enemies) {
        if (!enemy.eagle || enemy.eagle.phase === 'gone') continue;
        const def = level.enemies[enemy.index]!;
        if (enemy.eagle.warningTick === null) {
          if (current.player.x >= def.x) {
            if (startWarning(enemy.eagle, current.tick, { x: current.player.x, y: current.player.y })) {
              context.emit({ type: 'enemy-warning', enemyType: enemy.type, enemyIndex: enemy.index, targetY: current.player.y });
            }
          }
        } else if (stepEagle(enemy.eagle, current.tick)) {
          context.emit({ type: 'en41:dive', enemyIndex: enemy.index });
        }
        // Keep the enemy's x/y in sync with the dive for the hooks and the damage step.
        if (enemy.eagle.phase === 'diving' || enemy.eagle.phase === 'levelled') {
          enemy.eagle.diveTicks += 1;
        }
        const at = eaglePosition(enemy.eagle, current.player.x);
        if (at) {
          enemy.x = at.x;
          enemy.y = at.y;
          if (at.y <= GROUND_Y + 0.5 + 1e-9) enemy.eagle.phase = 'levelled';
          if (at.x < viewOf(run).left - 2) enemy.eagle.phase = 'gone';
        }
      }
    }
    // EN-33 (issue #118): the tequila train advances with the player (slower than the scroll, no INTERCEPT)
    // and its chimney emits smoke columns that never harm. It whistles once when it enters the screen.
    if (current.levelState === 'VUELO') {
      for (const enemy of run.enemies) {
        if (!enemy.train || enemy.activatedAt === null) continue;
        enemy.x = trainX(level.enemies[enemy.index]!.x, enemy.train.ticks + 1);
        const onScreen = run.entities.some((entity) => entity.kind === 'enemy' && entity.index === enemy.index && entity.active);
        if (onScreen && !enemy.train.whistled) {
          enemy.train.whistled = true;
          context.emit({ type: 'train:whistle', enemyIndex: enemy.index });
        }
        if (stepTrain(enemy.train, chimneyX(enemy.x), onScreen) !== null) {
          context.emit({ type: 'en33:smoke', enemyIndex: enemy.index });
        }
      }
    }
    // EN-32 (issue #117): Don Nieto throws a sombrero when his balcony is on screen and ahead of the plane;
    // with the Soborno active he looks away. The sombrero flies out and comes back to the balcony.
    if (current.levelState === 'VUELO') {
      for (const enemy of run.enemies) {
        if (!enemy.sombrero || enemy.activatedAt === null) continue;
        const onScreen = run.entities.some((entity) => entity.kind === 'enemy' && entity.index === enemy.index && entity.active);
        const ahead = onScreen && enemy.x > current.player.x;
        const events = stepSombreroThrower(enemy.sombrero, enemy, current.player.y, ahead, current.bribeRemaining > 0);
        if (events.windup) context.emit({ type: 'sombrero:windup', enemyIndex: enemy.index });
        if (events.thrown) context.emit({ type: 'sombrero:thrown', enemyIndex: enemy.index });
      }
    }
    // EN-11 (issue #87): its cycle counts from the first time it is in the view and goes on after it leaves.
    if (current.levelState === 'VUELO') {
      for (const enemy of run.enemies) {
        if (!enemy.hippo || enemy.activatedAt === null) continue;
        const step = stepHippo(enemy.hippo);
        enemy.y = step.y;
        if (step.bubbles) context.emit({ type: 'en11:bubbles', enemyIndex: enemy.index });
        if (step.jump) context.emit({ type: 'en11:jump', enemyIndex: enemy.index });
        if (step.splash) context.emit({ type: 'en11:splash', enemyIndex: enemy.index });
      }
    }
    // The boss (issue #89): it enters when the plane reaches the arena, stays at 80 % of the view and moves.
    if (run.boss && current.levelState === 'VUELO') {
      if (run.boss.status === 'entering' && current.player.x >= run.arenaStart) {
        enterArena(run.boss, { emit: (event) => context.emit(event) });
      }
      const view = viewOf(run);
      run.boss.x = bossX(view.left);
      if (run.attacks) stepAttacks(run, run.attacks, view);
      stepBossFight(run.boss, context, run.attacks ? { run, attacks: run.attacks, view } : undefined);
      if (run.boss.status === 'done' && level.def.ending === 'boss-and-jump') openUsSky(run, context);
    }
    // The jump zone starts once the US sky has been flown (issue #92).
    if (current.levelState === 'CIELO_EEUU' && run.usSky && current.player.x >= run.usSky.jumpX) {
      transition(run, 'JUMP_ZONE', context);
      run.jumpZone = {
        fromY: current.player.y,
        ticks: 0,
        viewLeft: viewOf(run).left,
        targetX: current.player.x + JUMP_ZONE.targetAhead,
      };
      context.emit({ type: 'jump:zone-entered', x: current.player.x });
    }
    // EN-02 (issue #84): its projectiles keep flying after the helicopter has left the screen.
    if (current.levelState === 'VUELO') {
      const { left, right } = viewOf(run);
      for (const enemy of run.enemies) if (enemy.helicopter) stepProjectiles(enemy.helicopter, { left: left - 2, right: right + 2, bottom: -1, top: 14 });
      // EN-44 (issue #130): the projectiles of a wall searchlight keep flying after it leaves the active window.
      for (const enemy of run.enemies) if (enemy.searchlight) stepWallProjectiles(enemy.searchlight, { left: left - 2, right: right + 2, bottom: -1, top: 14 });
    }
    for (const entity of run.entities) {
      if (!entity.active) continue;
      entity.updates += 1;
      const enemy = entity.kind === 'enemy' ? run.enemies[entity.index]! : undefined;
      if (enemy?.helicopter && current.levelState === 'VUELO') {
        const hover = level.enemies[entity.index]!.y;
        const helicopter = enemy.helicopter;
        if (helicopter.arrivalLeft > 0) {
          helicopter.arrivalLeft = Math.max(0, helicopter.arrivalLeft - EN02.arrivalSpeed / SIM_HZ);
          enemy.y = hover + helicopter.arrivalLeft;
        }
        const step = stepHelicopter(helicopter, enemy, current.player, current.bribeRemaining > 0);
        if (step.amber) context.emit({ type: 'en02:amber', enemyIndex: enemy.index });
        if (step.burst) context.emit({ type: 'en02:burst', enemyIndex: enemy.index, bursts: helicopter.bursts });
      }
      // EN-44 (issue #130): a fixed wall searchlight; its sweep is a function of the global tick, so a group
      // sweeps in sync and the result is the same after a respawn.
      if (enemy?.searchlight && current.levelState === 'VUELO') {
        const step = stepWallSearchlight(enemy.searchlight, enemy, current.player, current.tick, current.bribeRemaining > 0);
        if (step.amber) context.emit({ type: 'en44:amber', enemyIndex: enemy.index });
        if (step.burst) context.emit({ type: 'en44:burst', enemyIndex: enemy.index, bursts: enemy.searchlight.bursts });
      }
      // EN-23 (#106): the leader of the flock moves like EN-01; the wingmen follow it (seagull-flock.ts).
      if ((enemy?.type === 'EN-01' || enemy?.type === 'EN-23') && current.levelState === 'VUELO') {
        const y0 = level.enemies[entity.index]!.y;
        Object.assign(enemy, stepGallinazo({ x: enemy.x, y: enemy.y, y0 }, current.tick - (enemy.activatedAt ?? current.tick)));
      }
    }
  });

  // Phase 2, after the flight physics (issue #92): in the US sky the ground holds the plane like the ceiling
  // (it costs no life); in the jump zone the autopilot brings it to its altitude in 1 s and flies level.
  core.systems.register(TickPhase.Physics, (current, context) => {
    if (current.levelState === 'CIELO_EEUU' && groundContact(current.player)) {
      current.player = { ...current.player, y: GROUND_REST_Y, vy: Math.max(0, current.player.vy) };
    }
    // The pilot falls, opens the parachute and lands (#93): the landing grades the jump and ends the level.
    if (current.levelState === 'SALTO' && run.jump) {
      const before = run.jump.phase;
      current.player = stepPilot(run.jump, current.player);
      // The parachute opens by itself 0.5 s after the jump (#100: its sound follows this event).
      if (before === 'falling' && run.jump.phase === 'parachute') context.emit({ type: 'jump:parachute', x: current.player.x, y: current.player.y });
      if (run.jump.phase === 'landed' && !current.levelCompleted) land(run, context);
      return;
    }
    if (current.levelState === 'SALTO' && run.jumpZone) {
      run.jumpZone.ticks += 1;
      const settle = Math.min(1, run.jumpZone.ticks / (JUMP_ZONE.autopilotSettleSeconds * SIM_HZ));
      const { fromY } = run.jumpZone;
      current.player = { ...current.player, y: fromY + (JUMP_ZONE.autopilotAltitude - fromY) * settle, vy: 0 };
    }
  });

  // Phase 4: a column is passed when the tail of the plane clears it; COLUMN_POINTS, once (GDD §12).
  core.systems.register(TickPhase.PickupsAndTriggers, (current, context) => {
    const plane = playerAabb(current.player);
    for (const { index } of activeColumns()) {
      if (run.passedColumns.has(index) || !hasPassed(plane, level.columns[index]!, columnWidth)) continue;
      run.passedColumns.add(index);
      current.pendingScore += COLUMN_POINTS;
      context.emit({ type: 'COLUMN_PASSED', columnIndex: index });
    }
  });

  // Phase 4: checkpoints, with their CHECKPOINT_POINTS bonus (GDD §12). The event carries what the
  // immediate save needs (#47), bonus included.
  core.systems.register(TickPhase.PickupsAndTriggers, (current, context) => {
    if (current.levelCompleted) return;
    const checkpoint = crossCheckpoints(run.checkpoints, current.player.x);
    if (!checkpoint) return;
    run.state.checkpointId = checkpoint.id;
    current.pendingScore += CHECKPOINT_POINTS;
    context.emit({
      type: 'CHECKPOINT_ACTIVATED',
      checkpointId: checkpoint.id,
      radioLine: CHECKPOINT_RADIO_LINE,
      score: current.score + current.pendingScore,
      dollars: current.dollars,
      lives: current.lives,
    });
  });

  // Phase 4: pickups (GDD §6.1, §7, §12), one by one: dollar bundles, armor plates, briefcases and scapulars.
  core.systems.register(TickPhase.PickupsAndTriggers, (current, context) => {
    const plane = playerAabb(current.player);
    for (const entity of run.entities) {
      if (entity.kind !== 'pickup' || !entity.active || run.collectedPickups.has(entity.index)) continue;
      const pickup = level.pickups[entity.index]!;
      if (!isCollectable(plane, pickup)) continue;
      run.collectedPickups.add(entity.index);
      if (pickup.type === 'armor') {
        collectArmor(current, context);
        continue;
      }
      if (pickup.type === 'bribe') {
        collectBribe(current, context);
        continue;
      }
      if (pickup.type === 'scapular') {
        collectScapular(current, context);
        continue;
      }
      collectDollar(current, context);
      context.emit({ type: 'DOLLAR_COLLECTED', index: entity.index, dollars: current.dollars });
    }
  });

  // Phase 4, last: the level end (GDD §6.2 rule 7), after the checkpoints and pickups of the same tick.
  // Its checkpoints are cleared and the progression applied;
  // the damaging collisions of the same tick are ignored (#30). Airstrip (#72) and El Salto (#92, #93)
  // will replace this simple finish line.
  core.systems.register(TickPhase.PickupsAndTriggers, (current, context) => {
    if (current.levelState !== 'VUELO' || current.levelCompleted || current.player.x < level.length) return;
    finishLevel(run, context, null);
  });

  // Phase 6: a life lost in this attempt cancels the clean run; gaining one back does not restore it.
  core.systems.register(TickPhase.TimersAndScore, (current) => {
    if (current.deaths !== run.deathsSeen) run.state.cleanRun = false;
    run.deathsSeen = current.deaths;
  });

  // Phase 6: a crash detected by the lives (#37) enters DERRIBO; when DERRIBO ends, RESPAWN with lives
  // left or FAIL on the last one (game over, #40).
  core.systems.register(TickPhase.TimersAndScore, (current, context) => {
    if (current.levelState === 'DERRIBO' && run.observedState === 'VUELO') {
      run.observedState = 'DERRIBO';
      run.derriboRemaining = DERRIBO_TICKS;
      context.emit({ type: 'LEVEL_STATE_CHANGED', from: 'VUELO', to: 'DERRIBO' });
      return;
    }
    if (current.levelState !== 'DERRIBO' || run.derriboRemaining <= 0) return;
    run.derriboRemaining -= 1;
    if (run.derriboRemaining === 0) transition(run, current.lives > 0 ? 'RESPAWN' : 'FAIL', context);
  });

  // Phase 5: touching an active enemy is a damaging collision; nobody is destroyed (GDD §8.1).
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    const plane = playerAabb(current.player);
    for (const entity of run.entities) {
      if (entity.kind !== 'enemy' || !entity.active) continue;
      const enemy = run.enemies[entity.index]!;
      // Landed paragliders and hippos in the water or bubbling do not hurt.
      if (!isEnemyType(enemy.type) || enemy.paraglider?.landed) continue;
      if (enemy.hippo && enemy.hippo.phase !== 'airborne') continue;
      // The port crane, the seagull flock, the antenna (harmless; its plane is checked below) and the wall
      // searchlight (fixed on the wall, no body of its own) have their own shapes or no collider.
      if (enemy.crane || enemy.antenna || enemy.type === 'EN-23' || enemy.searchlight || enemy.type === 'EN-44') continue;
      const tuning = ENEMIES[enemy.type];
      const hit =
        'hitboxRadius' in tuning
          ? circleAabbOverlap(createCircle(enemy.x, enemy.y, tuning.hitboxRadius), plane)
          : aabbOverlap(createAabb(enemy.x, enemy.y, tuning.hitboxWidth, tuning.hitboxHeight), plane);
      if (hit) {
        context.emit({ type: 'DAMAGING_COLLISION', source: 'enemy', enemyType: enemy.type, enemyIndex: enemy.index });
      }
    }
  });

  // Phase 4: the decoys spawn, are picked up and fly to the boss (issue #90).
  core.systems.register(TickPhase.PickupsAndTriggers, (current, context) => {
    if (run.decoys && current.levelState === 'VUELO') stepDecoys(run, run.decoys, context, viewOf(run).left);
    if (run.attacks && current.levelState === 'VUELO') collectAttackPickups(run, run.attacks, context);
  });

  // Phase 5: damaging objects of the boss: leads, plush hippos and the projectiles of its helicopter (issue #95).
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    if (run.attacks && current.levelState === 'VUELO') attackCollisions(run, run.attacks, context);
  });

  // Phase 5: touching the body of the boss is a damaging collision (GDD §9.1); a retreating boss does not hurt.
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    if (!run.boss || !isDamaging(run.boss) || !isVulnerable(run)) return;
    const body = bossBody(run.boss);
    if (aabbOverlap(createAabb(body.x, body.y, body.width, body.height), playerAabb(current.player))) {
      context.emit({ type: 'DAMAGING_COLLISION', source: 'boss', bossId: run.boss.def.id });
    }
  });

  // Phase 4: an antenna triggers when the hitbox of the plane touches its zone, once, and not under the bribe (#105).
  core.systems.register(TickPhase.PickupsAndTriggers, (current, context) => {
    if (current.bribeRemaining > 0) return;
    const plane = playerAabb(current.player);
    for (const enemy of run.enemies) {
      if (enemy.antenna?.phase !== 'IDLE' || !touchesZone(enemy.x, enemy.y, plane)) continue;
      enemy.antenna.phase = 'TRIGGERED';
      enemy.antenna.triggeredAt = current.tick;
      context.emit({ type: 'SURVEILLANCE_TRIGGERED', enemyIndex: enemy.index });
    }
  });

  // Phase 5: the hitman plane called by an antenna (issue #105), with the EN-03 hitbox.
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    const plane = playerAabb(current.player);
    for (const enemy of run.enemies) {
      const hitman = enemy.antenna?.hitman;
      if (hitman && aabbOverlap(createAabb(hitman.x, hitman.y, EN03.hitboxWidth, EN03.hitboxHeight), plane)) {
        context.emit({ type: 'DAMAGING_COLLISION', source: 'enemy', enemyType: 'EN-03', enemyIndex: enemy.index });
      }
    }
  });

  // Phase 5: the seagull flock (issue #106): three circles; two contacts on one tick still cost one life.
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    const plane = playerAabb(current.player);
    for (const entity of run.entities) {
      if (entity.kind !== 'enemy' || !entity.active) continue;
      const enemy = run.enemies[entity.index]!;
      if (enemy.type !== 'EN-23') continue;
      const params = level.enemies[enemy.index]!.params;
      if (flockCircles(enemy.x, enemy.y, params).some((circle) => circleAabbOverlap(circle, plane))) {
        context.emit({ type: 'DAMAGING_COLLISION', source: 'enemy', enemyType: enemy.type, enemyIndex: enemy.index });
      }
    }
  });

  // Phase 5: the port crane (issue #104): its mast and beam, and its arm where it is on this tick, so an arm
  // that reaches the plane hurts on that same tick.
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    const plane = playerAabb(current.player);
    for (const entity of run.entities) {
      if (entity.kind !== 'enemy' || !entity.active) continue;
      const enemy = run.enemies[entity.index]!;
      if (!enemy.crane) continue;
      const { angle } = craneView(enemy.crane.ticks, level.enemies[enemy.index]!.params);
      const hit =
        craneStructure(enemy.x, enemy.y).some((piece) => aabbOverlap(piece, plane)) ||
        armCircles(enemy.x, enemy.y, angle).some((circle) => circleAabbOverlap(circle, plane));
      if (hit) context.emit({ type: 'DAMAGING_COLLISION', source: 'enemy', enemyType: enemy.type, enemyIndex: enemy.index });
    }
  });

  // Phase 5: the toxic cloud of the crop duster (issue #116): a puff that touches the plane is a damaging collision, as an
  // enemy projectile (the bribe protects, the armor absorbs one contact).
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    const plane = playerAabb(current.player);
    for (const enemy of run.enemies) {
      for (const puff of enemy.cropDuster?.puffs ?? []) {
        if (circleAabbOverlap(puffCircle(puff), plane)) {
          context.emit({ type: 'DAMAGING_COLLISION', source: 'projectile', enemyType: 'EN-31', enemyIndex: enemy.index });
        }
      }
    }
  });

  // Phase 5: the barrel of a tunnel (EN-43) that touches the plane is a damaging collision (issue #129). The dust
  // and the trapdoor never harm.
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    const plane = playerAabb(current.player);
    for (const enemy of run.enemies) {
      if (enemy.tunnel?.phase !== 'launched') continue;
      if (circleAabbOverlap(barrelCircle(enemy.x, barrelY(enemy.tunnel.flight)), plane)) {
        context.emit({ type: 'DAMAGING_COLLISION', source: 'enemy', enemyType: enemy.type, enemyIndex: enemy.index });
      }
    }
  });

  // Phase 5: a projectile of a wall searchlight (EN-44) that touches the plane is a damaging collision (issue #130).
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    const plane = playerAabb(current.player);
    for (const enemy of run.enemies) {
      for (const projectile of enemy.searchlight?.projectiles ?? []) {
        if (projectile.delay > 0) continue;
        if (circleAabbOverlap(createCircle(projectile.x, projectile.y, EN02.projectileRadius), plane)) {
          context.emit({ type: 'DAMAGING_COLLISION', source: 'projectile', enemyType: enemy.type, enemyIndex: enemy.index });
        }
      }
    }
  });

  // Phase 5: the border drone (EN-42) that touches the plane is a damaging collision (issue #128). It is not
  // destroyed; the armor absorbs it and the bribe ignores it (the general enemy rules apply).
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    const plane = playerAabb(current.player);
    for (const enemy of run.enemies) {
      if (!enemy.drone) continue;
      if (aabbOverlap(droneBody(enemy.x, enemy.y), plane)) {
        context.emit({ type: 'DAMAGING_COLLISION', source: 'enemy', enemyType: enemy.type, enemyIndex: enemy.index });
      }
    }
  });

  // Phase 5: the diving eagle that touches the plane is a damaging collision (issue #127). It is harmless during
  // the warning (it is not in the playfield yet) and once it has levelled off and left.
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    const plane = playerAabb(current.player);
    for (const enemy of run.enemies) {
      if (!enemy.eagle || (enemy.eagle.phase !== 'diving' && enemy.eagle.phase !== 'levelled')) continue;
      if (circleAabbOverlap(eagleCircle(enemy), plane)) {
        context.emit({ type: 'DAMAGING_COLLISION', source: 'enemy', enemyType: enemy.type, enemyIndex: enemy.index });
      }
    }
  });

  // Phase 5: touching the body of the tequila train is a damaging collision (issue #118). The smoke never
  // harms and never collides: it is not in this phase at all.
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    const plane = playerAabb(current.player);
    for (const enemy of run.enemies) {
      if (!enemy.train) continue;
      if (aabbOverlap(trainBody(enemy.x), plane)) {
        context.emit({ type: 'DAMAGING_COLLISION', source: 'enemy', enemyType: enemy.type, enemyIndex: enemy.index });
      }
    }
  });

  // Phase 5: a sombrero of EN-32 that touches the plane is a damaging collision (issue #117), harmful on the
  // outbound and the return leg alike.
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    const plane = playerAabb(current.player);
    for (const enemy of run.enemies) {
      if (!enemy.sombrero?.sombrero) continue;
      const at = sombreroPosition(enemy.sombrero, enemy);
      if (at && circleAabbOverlap(sombreroCircle(at), plane)) {
        context.emit({ type: 'DAMAGING_COLLISION', source: 'enemy', enemyType: enemy.type, enemyIndex: enemy.index });
      }
    }
  });

  // Phase 5: a projectile of the helicopter that touches the plane is a damaging collision (issue #84).
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    const plane = playerAabb(current.player);
    for (const enemy of run.enemies) {
      for (const projectile of enemy.helicopter?.projectiles ?? []) {
        if (projectile.delay > 0) continue;
        if (circleAabbOverlap(createCircle(projectile.x, projectile.y, EN02.projectileRadius), plane)) {
          context.emit({ type: 'DAMAGING_COLLISION', source: 'projectile', enemyType: 'EN-02', enemyIndex: enemy.index });
        }
      }
    }
  });

  // Phase 5: touching a piece of an active column is a damaging collision (lives react in #37).
  core.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    const plane = playerAabb(current.player);
    for (const { index } of activeColumns()) {
      if (collidesWithColumn(plane, pieces[index]!)) {
        context.emit({ type: 'DAMAGING_COLLISION', source: 'column', columnIndex: index });
      }
    }
  });
  // Phase 6, last: the INTERCEPT warnings of this tick (issue #83).
  core.systems.register(TickPhase.TimersAndScore, (_current, context) => updateIntercepts(run, context));
  updateActivation(run);
  return run;
}
